import { API_BASE_URL as RUNTIME_API_BASE_URL, apiUrl } from '../config/runtime';

const BASE = RUNTIME_API_BASE_URL;
export const API_BASE_URL = BASE;

// ── Core fetch wrapper ────────────────────────────────────────────────────────
async function call(method, path, body, opts = {}) {
  const token = localStorage.getItem('dd_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(apiUrl(path), {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal: opts.signal,
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data   = data;
    throw err;
  }
  return data;
}

const get    = (path, qs, opts) => call('GET',    path + (qs ? '?' + new URLSearchParams(qs).toString() : ''), undefined, opts);
const post   = (path, body)   => call('POST',   path, body);
const patch  = (path, body)   => call('PATCH',  path, body);
const del    = (path)         => call('DELETE',  path);

const makeFallbackUsername = (fullName, email) => {
  const base = String(fullName || email || 'user')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 16);
  const suffix = Math.random().toString(36).slice(2, 6);
  return `${base || 'user'}_${suffix}`.slice(0, 20);
};

// ─────────────────────────────────────────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────────────────────────────────────────
export const auth = {
  /** Register new account. Returns either { message, email } or { token, user } */
  register: async ({ email, full_name, password }) => {
    let data;
    try {
      data = await post('/auth/register', { email, full_name, password });
    } catch (err) {
      if (![400, 422, 502].includes(err?.status)) throw err;
      data = await post('/auth/register', {
        email,
        password,
        username: makeFallbackUsername(full_name, email),
      });
    }
    if (data.token) localStorage.setItem('dd_token', data.token);
    return data;
  },

  /** Login. Stores token. Returns { token, user } */
  login: async ({ email, password }) => {
    let data;
    try {
      data = await post('/auth/login', { email, password });
    } catch (err) {
      if (err?.status !== 502) throw err;
      data = await post('/auth/login', { email: { email, password } });
    }
    if (data.token) localStorage.setItem('dd_token', data.token);
    return data;
  },

  /** Fetch current user (validates token). Returns { user } */
  me: () => get('/auth/me'),

  /** Verify email from link */
  verifyEmail: (token) => get('/auth/verify-email', { token }),

  /** Verify signup code. Stores token when backend returns it. */
  verifyEmailCode: async ({ email, code }) => {
    const data = await post('/auth/verify-email-code', { email, code });
    if (data.token) localStorage.setItem('dd_token', data.token);
    return data;
  },

  /** Resend signup verification code */
  resendVerificationCode: (email) => post('/auth/resend-verification-code', { email }),

  /** Send password reset email */
  forgotPassword: (email) => post('/auth/forgot-password', { email }),

  /** Reset password with token */
  resetPassword: (token, password) => post('/auth/reset-password', { token, password }),

  /** Clear stored token */
  logout: () => localStorage.removeItem('dd_token'),

  /** Check if logged in */
  isLoggedIn: () => !!localStorage.getItem('dd_token'),

  /** Permanently delete account (requires password confirmation) */
  deleteAccount: (password) => call('DELETE', '/users/me', { password }),
};



// ─────────────────────────────────────────────────────────────────────────────
// USERS
// ─────────────────────────────────────────────────────────────────────────────
export const users = {
  /** Get public profile by username. Returns { user, timeline } */
  profile: (username) => get(`/users/${username}`),

  /**
   * Update own profile.
   * Fields: bio, location, website, username
   */
  updateMe: (data) => patch('/users/me', data),

  /** Get current user's saved posts. Returns { posts } */
  savedPosts: (params = {}) => get('/users/me/saved', params),
};

// ─────────────────────────────────────────────────────────────────────────────
// HEALTH
// ─────────────────────────────────────────────────────────────────────────────
export const health = {
  check: () => get('/health'),
};

// USAGE
export const usage = {
  /** Record a usage event. Body: { module_id, session_id, kind, step?, units?, reason?, meta? } */
  event: (payload) => post('/usage/event', payload),

  /** Get usage summary for the current period. Returns { period, tier, is_premium, modules, total_done } */
  summary: (params = {}) => get('/usage/summary', params),
};

// PAYMENTS
export const payments = {
  /** Create a Lemon Squeezy checkout for a plan. Body: { plan } */
  checkout: (plan) => post('/payments/checkout', { plan }),

  /** Get current billing status for the logged-in user. */
  status: (params = {}, opts = {}) => get('/payments/status', params, opts),
};

// WORKSPACE
export const workspace = {
  save: (payload) => post('/workspace/save', payload),
  update: (id, updates) => patch(`/workspace/${id}`, updates),
  get: (id, qs) => get(`/workspace/${id}`, qs),
  list: () => get('/workspace/list'),
  remove: (id) => del(`/workspace/${id}`),
};

// PAPERS — AI analysis cache (paid tier)
export const papers = {
  /** Check if a paper has already been AI-analyzed. Returns { cached, ai_data?, monthly_used }. */
  checkCache: (hash) => get('/papers/ai-cache', { hash }),
  /** Run AI analysis. Requires paid tier. Returns { ai_data, from_cache, monthly_used }. */
  runAI: (contentHash, rawText) => post('/papers/ai-run', { content_hash: contentHash, raw_text: rawText }),
};

// ─────────────────────────────────────────────────────────────────────────────
// PUBLISHED EXPERIMENTS
// ─────────────────────────────────────────────────────────────────────────────
export const experiments = {
  /** Publish a new experiment. Body: { type, title, description, tags, visibility, snapshot, id? } */
  publish: (payload) => post('/experiments', payload),

  /** Update an existing published experiment (own only). */
  update: (id, updates) => patch(`/experiments/${id}`, updates),

  /** List own experiments (auth required). Returns { experiments, count, cap } */
  list: () => get('/experiments'),

  /** Public feed — no auth. Params: { type?, tag?, limit? } */
  publicFeed: (params = {}) => get('/experiments/public', params),

  /** Get a single experiment by id. */
  getOne: (id) => get(`/experiments/${id}`),

  /** Toggle upvote on a public experiment. */
  upvote: (id) => post(`/experiments/${id}/upvote`),

  /** Toggle star on a public experiment. */
  star: (id) => post(`/experiments/${id}/star`),

  /** Load comments for a public experiment. */
  comments: (id) => get(`/experiments/${id}/comments`),

  /** Add a comment to a public experiment. */
  addComment: (id, body) => post(`/experiments/${id}/comments`, { body }),

  /** Delete own experiment. */
  remove: (id) => del(`/experiments/${id}`),
};

// Default export for convenience
export default {
  auth, users, health, usage, payments, workspace, experiments
};
